<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { usersApi, type CustomerNote } from '@/api/users.api'
import { formatShortDate } from '@/utils/customers'

const props = defineProps<{
  userId: string
}>()

const notes = ref<CustomerNote[]>([])
const loading = ref(false)
const error = ref('')
const draft = ref('')
const saving = ref(false)
const saveError = ref('')

function formatNoteDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return formatShortDate(value)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

async function loadNotes() {
  loading.value = true
  error.value = ''
  try {
    const res = await usersApi.listNotes(props.userId)
    if (res.status !== 200) {
      error.value =
        (res.data as { detail?: string } | undefined)?.detail || 'Failed to load notes'
      notes.value = []
      return
    }
    notes.value = res.data?.notes ?? []
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : 'Failed to load notes'
    notes.value = []
  } finally {
    loading.value = false
  }
}

async function addNote() {
  const body = draft.value.trim()
  if (!body || saving.value) return
  saving.value = true
  saveError.value = ''
  try {
    const res = await usersApi.createNote(props.userId, body)
    if (res.status !== 200 && res.status !== 201) {
      saveError.value =
        (res.data as { detail?: string } | undefined)?.detail || 'Failed to save note'
      return
    }
    if (res.data) {
      notes.value = [res.data, ...notes.value]
    } else {
      await loadNotes()
    }
    draft.value = ''
  } catch (e: unknown) {
    saveError.value = e instanceof Error ? e.message : 'Failed to save note'
  } finally {
    saving.value = false
  }
}

watch(
  () => props.userId,
  () => {
    draft.value = ''
    saveError.value = ''
    void loadNotes()
  },
)

onMounted(() => {
  void loadNotes()
})
</script>

<template>
  <section class="mt-5 border-t border-gray-100 pt-5">
    <div class="flex items-baseline justify-between gap-3">
      <h3 class="text-xs font-medium uppercase tracking-wide text-gray-400">Notes</h3>
      <span v-if="!loading" class="text-xs tabular-nums text-gray-400">{{ notes.length }}</span>
    </div>

    <form class="mt-3" @submit.prevent="addNote">
      <textarea
        v-model="draft"
        rows="3"
        placeholder="Add a note about this customer…"
        class="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest"
      />
      <div class="mt-2 flex items-center justify-between gap-3">
        <p v-if="saveError" class="text-xs text-red-600">{{ saveError }}</p>
        <span v-else class="text-xs text-gray-400">Visible to the Lightbase team</span>
        <button
          type="submit"
          class="shrink-0 rounded-lg bg-forest px-3 py-1.5 text-xs font-medium text-white hover:bg-forest/90 disabled:opacity-50"
          :disabled="saving || !draft.trim()"
        >
          {{ saving ? 'Saving…' : 'Add note' }}
        </button>
      </div>
    </form>

    <p v-if="loading" class="mt-4 text-center text-sm text-gray-400">Loading notes…</p>
    <p v-else-if="error" class="mt-4 text-center text-sm text-red-600">{{ error }}</p>
    <p v-else-if="!notes.length" class="mt-4 text-center text-sm text-gray-400">
      No notes yet.
    </p>
    <ul v-else class="mt-4 space-y-3">
      <li
        v-for="note in notes"
        :key="note.id"
        class="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5"
      >
        <div class="flex items-baseline justify-between gap-3">
          <time class="text-xs font-medium text-gray-500" :datetime="note.created_at">
            {{ formatNoteDate(note.created_at) }}
          </time>
          <span v-if="note.author_email" class="truncate text-[11px] text-gray-400">
            {{ note.author_email }}
          </span>
        </div>
        <p class="mt-1.5 whitespace-pre-wrap text-sm text-gray-800">{{ note.body }}</p>
      </li>
    </ul>
  </section>
</template>
